import type React from 'react';
import { useCallback, useState } from 'react';
import type { CartItem } from '../../Store/CartItem/state';
import styles from './ProductOverview.module.scss';
import Icon from '../../../../stories/Atoms/Icon/Icon';
import AmountSelector from '../AmountSelector/AmountSelector';
import DeleteModal from '../Modal/DeleteModal';
import Backdrop from '../../../../stories/Atoms/Backdrop/Backdrop';
import FormatPrice from '../../../general/Format/FormatPrice';
import type {
  UpsellGroupProps,
  GiftCardProps,
  UpsellProduct,
} from '../../Store/Upsell/state';
import Translation from '../../../general/Translation/Translation';
import Picture from '../../../general/Picture/Picture';
import ProductModalPresenter from '../Modal/ProductModalPresenter';
import ButtonStack from '../../../general/Button/ButtonStack';
import GiftCard from '../Giftcard/GiftCard';
import UpsellSummary from '../Upsell/UpsellSummary';
import UpsellOverview from '../Upsell/UpsellOverview';
import Source from '../../../general/Picture/Source';

interface ProductOverviewProps {
  availableUpsellGroups: UpsellGroupProps[];
  giftCard: GiftCardProps;
  lazyLoadPreview: boolean;
  onAmountChange: (newAmount: string) => void;
  onProductDelete: () => void;
  onProductMessageDelete: () => void;
  product: CartItem;
}

const ProductOverview: React.FC<ProductOverviewProps> = ({
  availableUpsellGroups,
  lazyLoadPreview,
  onAmountChange,
  onProductDelete,
  product,
}) => {
  const [showDelete, setShowDelete] = useState<boolean>(false);

  const showDeleteModal = () => {
    setShowDelete(true);
  };

  const deleteProduct = () => {
    onProductDelete();
    setShowDelete(false);
  };

  const closeDeleteModal = () => {
    setShowDelete(false);
  };

  const getUpsellsInCart = useCallback(
    () =>
      availableUpsellGroups.reduce((upsellProducts, upsellGroup) => {
        const upsellsInGroupInCart = upsellGroup.products.filter(
          (upsellProduct) => upsellProduct.inCart,
        );

        return [...upsellProducts, ...upsellsInGroupInCart];
      }, [] as UpsellProduct[]),
    [availableUpsellGroups],
  );

  const discountDisplay =
    product.isDiscounted &&
    product.price.value < product.originalPrice.value ? (
      <span
        className={styles['ProductPrice__original-price']}
        data-testid="original-price"
      >
        <FormatPrice
          price={{
            currency: product.price.currency,
            value: product.originalPrice.value * product.amount,
          }}
        />
      </span>
    ) : (
      ''
    );

  return (
    <>
      <div className={styles.ProductOverview__wrapper}>
        <div className={styles.ProductOverview}>
          {showDelete && (
            <Backdrop onClose={closeDeleteModal}>
              <DeleteModal
                onAccept={deleteProduct}
                onClose={closeDeleteModal}
              />
            </Backdrop>
          )}

          <div
            className={styles.ProductOverview__header}
            data-clarity-mask="True"
          >
            {product.previewImage && (
              <ProductModalPresenter product={product}>
                <Picture
                  lazyLoad={lazyLoadPreview}
                  imageUrl={product.previewImage}
                  alt={product.name}
                >
                  <Source imageWidth={200} />
                </Picture>
              </ProductModalPresenter>
            )}
          </div>
          <div className={styles.ProductOverview__container}>
            <div className={styles['ProductOverview__container_product-info']}>
              <a
                className={styles.ProductOverview__container_title}
                href={product.productUrl}
              >
                {product.name}
              </a>
              {product.shippingText && (
                <p className={styles.ProductOverview__container_text}>
                  <Icon
                    style="fa-solid"
                    name="fa-circle-check"
                    size="fa-lg"
                    className="mr-2 c-secondary-02"
                  />
                  {product.shippingText}
                </p>
              )}
              <div className={styles.ProductPrice}>
                <div>
                  <div className={styles.ProductPrice__edit}>
                    <button
                      type="button"
                      className="button button--ghost button--clean button--xs button--has-icon"
                      onClick={showDeleteModal}
                      aria-label={`Delete ${product.name}`}
                    >
                      <Icon name="fa-trash-can" />
                    </button>
                    {product.amountMutable && (
                      <AmountSelector
                        product={product}
                        onAmountChange={onAmountChange}
                      />
                    )}
                  </div>
                </div>
                <div>
                  <span
                    translate="no"
                    className={styles.ProductPrice__column}
                  >
                    {discountDisplay}
                    <span data-testid="actual-price">
                      <FormatPrice
                        price={{
                          currency: product.price.currency,
                          value: product.price.value * product.amount,
                        }}
                      />
                    </span>
                  </span>
                </div>
              </div>
              <ButtonStack
                isMd
                isRow
                isStackedXs
              >
                {product.editUrl && (
                  <a
                    href={product.editUrl}
                    className="button button--link lh--normal"
                  >
                    <Translation
                      pageString="winkelmand_teksten"
                      stringId="change_personalisation"
                    />
                  </a>
                )}
                {product.copyPersonalisationUrl && (
                  <a
                    href={product.copyPersonalisationUrl}
                    className="button button--link lh--normal"
                  >
                    <Translation
                      pageString="winkelmand_teksten"
                      stringId="make_another_one"
                    />
                  </a>
                )}
              </ButtonStack>
            </div>
          </div>
        </div>
        {product.giftCard.active && (
          <GiftCard
            cartItemId={product.id}
            maxCharacters={product.giftCard.maxLength}
            text={product.giftCard.message}
          />
        )}
      </div>
      <>
        {Boolean(availableUpsellGroups.length) && (
          <UpsellSummary productUpsells={getUpsellsInCart()} />
        )}
        {Boolean(availableUpsellGroups.length) && (
          <UpsellOverview
            availableUpsellGroups={availableUpsellGroups}
            parentProductId={product.productId}
            parentCartItemId={product.id}
          />
        )}
      </>
    </>
  );
};

export default ProductOverview;
